import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import Slider from 'react-slick';
import dayjs from 'dayjs';
import { makeStyles } from '@material-ui/styles';
import { Box, Typography } from '@material-ui/core';

import Hyphenate from '../../../utils/hyphenate';
import { addEllipsis } from '../../../utils/textEllipsis';

import { Translation } from '../../common';

import trophy from '../../../assets/img/status/paid_trophy.png';

const TIMELINE_CAROUSEL_HEADER = isClientPortal =>
  `TIMELINE_CAROUSEL_HEADER${isClientPortal ? '_CLIENT' : ''}`;
const TIMELINE_CAROUSEL_BOTTOM = (isClientPortal, isPaidFully) =>
  `BOX_TIMELINE${isClientPortal ? '_CLIENT' : isPaidFully ? '_PAID' : ''}`;

const SEPARATOR_REGEX = /([\n\t\- .,;:?!]+)/g;

const formatSummary = summary => {
  if (!summary || !summary.length) {
    return null;
  }

  return summary.split(SEPARATOR_REGEX).map((word, idx, arr) => {
    const wordWithSpace = !SEPARATOR_REGEX.test(word)
      ? word + (idx !== arr.length - 1 ? arr[idx + 1] : '')
      : null;
    return wordWithSpace ? (
      wordWithSpace.length > 20 ? (
        <span key={idx}>
          <Hyphenate key={idx}>{wordWithSpace}</Hyphenate>
        </span>
      ) : (
        <span key={idx}>{wordWithSpace}</span>
      )
    ) : null;
  });
};

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    marginTop: 32,
    background: theme.palette.background.default,
    borderRadius: 16,
    padding: '24px 40px 32px 40px',
    textAlign: 'center'
  },
  rootCompact: {
    padding: '24px 21px 32px 21px'
  },
  rootSmall: {
    marginTop: 16,
    padding: '24px 16px 24px 16px'
  },
  item: {
    margin: '0 auto',
    position: 'relative',
    display: 'flex !important',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: 32,
    textAlign: 'center',
    outline: 'none'
  },
  circle: {
    position: 'absolute',
    top: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 8,
    height: 8,
    background: theme.palette.text.disabled,
    border: `2px solid ${theme.palette.text.hint}`,
    borderRadius: '100%'
  },
  circleCurrent: {
    background: theme.palette.primary.main,
    borderColor: theme.palette.primary.main
  },
  circlePaid: {
    background: theme.palette.secondary.main,
    borderColor: theme.palette.secondary.main
  },
  logo: {
    height: 40,
    padding: '0 8px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  trophy: {
    height: 32
  },
  image: {
    maxWidth: 138,
    maxHeight: 40
  },
  details: {
    flexGrow: 1,
    minHeight: 48,
    width: 138,
    margin: `16px auto 0 auto`,
    padding: 8,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: theme.palette.action.disabled,
    borderRadius: 8,
    boxSizing: 'border-box'
  },
  detailsCurrent: {
    background: theme.palette.primary.main
  },
  detailsPaid: {
    background: theme.palette.secondary.main
  },
  date: {
    color: theme.palette.text.secondary
  },
  dateCurrent: {
    color: theme.palette.primary.contrastText
  },
  datePaid: {
    color: theme.palette.text.disabled
  },
  dateFuture: {
    color: theme.palette.text.hint
  },
  summary: {
    width: '100%',
    maxHeight: 64,
    overflow: 'hidden',
    color: theme.palette.text.primary
  },
  summaryCurrentPaid: {
    ...theme.typography.subtitle2Bold,
    color: theme.palette.primary.contrastText
  },
  summaryFuture: {
    color: theme.palette.text.hint
  },
  explanation: {
    marginTop: 40
  },
  explanationSmall: {
    marginTop: 24
  }
}));

const TimelineItem = ({ item, currentIndex, index }) => {
  const classes = useStyles();
  const summaryRef = useRef();

  useEffect(() => {
    addEllipsis(summaryRef.current);
  }, []);

  const { logo, summary, datetime, label, current, type } = item;

  const isPaid = type === 'FULLY_PAID';
  const isFuture = currentIndex >= 0 && index > currentIndex && !isPaid;

  return (
    <Box className={classes.item} key={index}>
      <Box
        className={classNames(
          classes.circle,
          current && classes.circleCurrent,
          isPaid && classes.circlePaid
        )}
      />

      <Box className={classes.logo}>
        {isPaid && (
          <img
            className={classes.trophy}
            src={trophy}
            alt={summary}
            data-timeline-item-image
          />
        )}
        {!isPaid && logo && (
          <img
            className={classes.image}
            src={logo}
            alt={summary}
            data-timeline-item-image
          />
        )}
        {!isPaid && !logo && (
          <Box className={classes.image} data-timeline-item-image>
            {label}
          </Box>
        )}
      </Box>
      <Box
        className={classNames(
          classes.details,
          current && classes.detailsCurrent,
          isPaid && classes.detailsPaid
        )}
        data-timeline-item-details
      >
        {datetime && (
          <Typography
            variant="subtitle2"
            className={classNames(
              classes.date,
              current && classes.dateCurrent,
              isPaid && classes.datePaid,
              isFuture && classes.dateFuture
            )}
          >
            {dayjs(datetime).format('DD.MM.YYYY')}
          </Typography>
        )}
        <Typography
          ref={summaryRef}
          variant="subtitle2"
          className={classNames(
            classes.summary,
            (current || isPaid) && classes.summaryCurrentPaid,
            isFuture && classes.summaryFuture
          )}
          title={summary}
        >
          {formatSummary(summary)}
        </Typography>
      </Box>
    </Box>
  );
};

const TimelineSlider = ({
  isTabletSize,
  isMobileSize,
  settings,
  items,
  isClientPortal,
  isPaidFully
}) => {
  const classes = useStyles();
  let currentIndex = items.findIndex(i => !!i.current);

  return (
    <Box
      className={classNames(
        classes.root,
        isTabletSize && classes.rootCompact,
        isMobileSize && classes.rootSmall
      )}
    >
      <Typography variant="h4" color="textPrimary">
        <Translation inline>
          {TIMELINE_CAROUSEL_HEADER(isClientPortal)}
        </Translation>
      </Typography>
      <Slider {...settings}>
        {items.map((item, index) => (
          <TimelineItem
            key={index}
            item={item}
            currentIndex={currentIndex}
            index={index}
          />
        ))}
      </Slider>
      <Typography
        className={classNames(
          classes.explanation,
          isMobileSize && classes.explanationSmall
        )}
        variant={isMobileSize ? 'body2' : 'body1'}
        color="textSecondary"
      >
        <Translation inline>
          {TIMELINE_CAROUSEL_BOTTOM(isClientPortal, isPaidFully)}
        </Translation>
      </Typography>
    </Box>
  );
};

export default TimelineSlider;
